'use client';

import { initAll, setUserId } from '@amplitude/unified';
import { useUser } from '@youversion/next-auth-react';
import { useEffect } from 'react';

import { useAppLanguage } from '@/context/appLanguage';
import { captureException } from '@/lib/sentry';
import { AMPLITUDE_API_KEY } from '@/utils/env';

/**
 * Whether the SDK has been initialized for this page load. Module-scoped (not a
 * component ref) so it survives an unmount/remount of `AmplitudeEngagement` —
 * e.g. a sign-out → sign-in in the same tab — matching the SDK's own
 * module-singleton lifetime. The SDK is initialized once per page load.
 */
let hasInitialized = false;

/**
 * Test-only: reset the once-per-page-load init guard so each test starts fresh.
 * @internal
 */
export function resetAmplitudeInitForTests(): void {
  hasInitialized = false;
}

/**
 * Initializes Amplitude Guides & Surveys via the standalone unified SDK.
 *
 * Guides is the only active product: analytics autocapture is disabled so the
 * existing DATAMAN pipeline stays the sole behavioral feed into Amplitude, and
 * Session Replay / Experiment are dormant. Identity is `user_id` only — the
 * stringified YV user id — which Amplitude matches against server-side cohorts
 * built from the DATAMAN-imported events.
 *
 * No-op when NEXT_PUBLIC_AMPLITUDE_API_KEY is unset (local/unprovisioned envs).
 */
export default function AmplitudeEngagement(): null {
  const { user } = useUser();
  const { appLanguage } = useAppLanguage();

  useEffect(() => {
    if (hasInitialized || !AMPLITUDE_API_KEY) {
      return;
    }
    hasInitialized = true;
    try {
      const result = initAll(AMPLITUDE_API_KEY, {
        serverZone: 'US',
        analytics: { autocapture: false },
        sessionReplay: { sampleRate: 0 },
        engagement: { locale: appLanguage ?? 'en-US' },
      });
      // initAll returns Promise<void>; report async init failures too.
      void Promise.resolve(result).catch(error => {
        captureException(error, { tags: { source: 'AmplitudeEngagement.init' } });
      });
    } catch (error) {
      captureException(error, { tags: { source: 'AmplitudeEngagement.init' } });
    }
    // Locale is captured once at first init; the SDK is not re-initialized on locale change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!AMPLITUDE_API_KEY || !hasInitialized || !user?.id) {
      return;
    }
    try {
      setUserId(String(user.id));
    } catch (error) {
      captureException(error, { tags: { source: 'AmplitudeEngagement.setUserId' } });
    }
  }, [user?.id]);

  return null;
}
